'use memo'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { buildEventPayload } from '@sportninja/common/features/event/buildEventPayload'
import type {
  EventFormValues,
  SnEvent,
} from '@sportninja/common/features/event/types'
import {
  createEvent as createEventService,
  createScheduleEvent as createScheduleEventService,
  deleteEvent as deleteEventService,
  updateEvent as updateEventService,
} from '@sportninja/common/services/event'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import type { EventSeriesScope } from '../seriesScope'

interface UseEventMutationsArgs {
  /**
   * Team the event belongs to. Absent on the competition schedule, which
   * creates events owned by the schedule rather than by any team — there
   * `scheduleId` is given instead.
   */
  teamId?: string
  /**
   * Roster the event is created under. Required to create a TEAM event (that
   * endpoint is team+roster scoped); not needed for update/delete, which
   * address the event directly, nor for a schedule event. Callers that only
   * edit/delete (the event detail page) may omit it.
   */
  rosterId?: string
  /**
   * Schedule the event is created on — a competition, season or division.
   * Given INSTEAD of `teamId`/`rosterId` to create a schedule-owned event.
   * Takes precedence when both are somehow present.
   *
   * Also needed for an UPDATE, unlike `teamId`/`rosterId`: its presence is what
   * tells the payload builder the Team select was on the form, so a caller that
   * only edits (the event detail page/screen) must still pass it for a
   * competition event — see `resolveEventScheduleId`.
   */
  scheduleId?: string
}

/**
 * Create / update / delete mutations for events.
 *
 * Create has two shapes: a TEAM event (`teamId` + `rosterId`, posted under the
 * roster) and a SCHEDULE event (`scheduleId`, owned by the competition and
 * belonging to no team). Update and delete are the same either way — both
 * address the event by id, so neither needs to know which shape made it.
 *
 * Every mutation invalidates the schedule LIST as well as the detail query, so
 * a create/edit/delete is reflected wherever the event appears. The list is
 * cached per roster (`events.byRoster`), per team (`events.byTeam`), per
 * schedule (`events.bySchedule`) and per My Schedule filter set
 * (`events.byMembership`); all sit under the `events.list` prefix, so one
 * invalidation covers every one of them at once — including the season or
 * competition the user is not currently looking at, which would otherwise go
 * stale unnoticed.
 *
 * That prefix relationship is load-bearing and easy to break by re-rooting a
 * key, so it is pinned by a test.
 */
export const useEventMutations = ({
  teamId,
  rosterId,
  scheduleId,
}: UseEventMutationsArgs) => {
  const queryClient = useQueryClient()

  // The Team select is only rendered for a COMPETITION event, and the payload
  // must only speak about `team_id` when it was. `scheduleId` is the same
  // signal the form gates the field on, so the two cannot drift apart.
  //
  // The visibility toggle is the MIRROR image: team events only, so it reads
  // the same signal inverted. Deriving both from `scheduleId` here is what
  // keeps each field's payload gate tied to the condition that renders it.
  const isCompetitionForm = Boolean(scheduleId)
  const payloadOptions = {
    hasTeamField: isCompetitionForm,
    hasIsPublicField: !isCompetitionForm,
  }

  /** Drops every cached event list (all rosters, all teams, all seasons). */
  const invalidateEventLists = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.events.list() })

  const createMutation = useMutation({
    mutationFn: (values: EventFormValues): Promise<SnEvent> => {
      const payload = buildEventPayload(values, 'create', payloadOptions)

      // A schedule-owned event: the competition schedule has no team of its
      // own, so there is no roster to post under. Checked FIRST so a caller
      // that somehow has both does not fall into the roster guard below.
      if (scheduleId) {
        return createScheduleEventService(scheduleId, payload)
      }

      if (!teamId || !rosterId) {
        throw new Error(
          'Cannot create an event without either a schedule or a team roster.'
        )
      }
      return createEventService(teamId, rosterId, payload)
    },
    // A new event must appear in the schedule without a manual refresh.
    onSuccess: invalidateEventLists,
  })

  const updateMutation = useMutation({
    mutationFn: ({
      eventId,
      values,
      scope,
    }: {
      eventId: string
      values: EventFormValues
      scope?: EventSeriesScope
    }): Promise<SnEvent> =>
      updateEventService(
        eventId,
        buildEventPayload(values, 'update', payloadOptions),
        scope
      ),
    onSuccess: (updated, { eventId, scope }) => {
      // Invalidate the id that was EDITED — that is the query the detail page
      // is observing. A scoped save ('following'/'all') can return a
      // different occurrence than the one opened, so keying off `updated.id`
      // alone left the visible page showing pre-edit data.
      queryClient.invalidateQueries({
        queryKey: queryKeys.events.byId(eventId),
      })

      // Also refresh the returned occurrence when it is a different one.
      if (updated?.id && updated.id !== eventId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.events.byId(updated.id),
        })
      }

      // A scoped save rewrites occurrences this client has no ids for, so
      // every cached detail entry is suspect — drop them all rather than
      // guessing which ones moved.
      if (scope && scope !== 'this') {
        queryClient.invalidateQueries({ queryKey: queryKeys.events.all })
      }

      // Name, time and location all show in the row, so the list is stale too.
      invalidateEventLists()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: ({
      eventId,
      scope,
    }: {
      eventId: string
      scope?: EventSeriesScope
    }) => deleteEventService(eventId, scope),
    onSuccess: async (_data, { eventId }) => {
      const detailKey = queryKeys.events.byId(eventId)

      // The detail page is still MOUNTED at this point — navigation happens
      // after this resolves — so its byId query still has an active observer.
      // Cancel any in-flight fetch, then park a permanently-fresh `null` in
      // the cache instead of removing the entry: `removeQueries` on a live
      // query makes React Query refetch it at once, which is a guaranteed 404
      // for an event that no longer exists.
      await queryClient.cancelQueries({ queryKey: detailKey })
      queryClient.setQueryData(detailKey, null)

      // A 'following'/'all' delete removes occurrences this client never had
      // ids for, so the whole list cache goes rather than single entries.
      invalidateEventLists()
    },
  })

  return {
    createEvent: (values: EventFormValues) =>
      createMutation.mutateAsync(values),
    updateEvent: (
      eventId: string,
      values: EventFormValues,
      scope?: EventSeriesScope
    ) => updateMutation.mutateAsync({ eventId, values, scope }),
    removeEvent: (eventId: string, scope?: EventSeriesScope) =>
      deleteMutation.mutateAsync({ eventId, scope }),
    isDeleting: deleteMutation.isPending,
  }
}
